import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  RefreshCw,
  Plus,
  FolderTree,
  ChevronDown,
  ChevronRight,
  Layers,
  Building2,
  MapPin,
  List,
  Network,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { coreAPI } from '../../lib/api';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { OrgChartNodeComponent } from './OrgChartNode';
import { OrgChartConnectorGroup } from './OrgChartConnector';
import { NodeConfigPanel } from './NodeConfigPanel';
import {
  OrgChartNode,
  OrgChartData,
  NodeType,
  LAYOUT_CONSTANTS,
  NODE_TYPE_LABELS,
  NODE_COLORS,
  applyHierarchyLabels,
} from './types';

/** Simple English pluralizer: Company → Companies, Group → Groups */
function pluralize(word: string): string {
  if (word.endsWith('y') && !/[aeiou]y$/i.test(word)) return word.slice(0, -1) + 'ies';
  if (word.endsWith('s') || word.endsWith('x') || word.endsWith('sh') || word.endsWith('ch')) return word + 'es';
  return word + 's';
}

interface OrgChartViewProps {
  companyType?: 'customer' | 'supplier';
  onNodeSelect?: (node: OrgChartNode | null) => void;
  selectedNodeId?: string | null;
}

interface LayoutPosition {
  node: OrgChartNode;
  x: number;
  y: number;
  depth: number;
}

const NODE_ICONS: Record<NodeType, typeof Building2> = {
  group: FolderTree,
  division: Building2,
  company: MapPin,
  warehouse: FolderTree,
  location: MapPin,
};

interface ListNodeProps {
  node: OrgChartNode;
  depth: number;
  expandedNodes: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  onEdit: (node: OrgChartNode) => void;
  onDelete: (node: OrgChartNode) => void;
  onAddChild: (parent: OrgChartNode, childType: NodeType) => void;
}

function ListNode({ node, depth, expandedNodes, onToggleExpand, onEdit, onDelete, onAddChild }: ListNodeProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const colors = NODE_COLORS[node.type];
  const Icon = NODE_ICONS[node.type];
  const label = NODE_TYPE_LABELS[node.type];
  const location = node.metadata?.city && node.metadata?.country
    ? `${node.metadata.city}, ${node.metadata.country}`
    : node.metadata?.country || node.metadata?.headquarters_country || '';

  return (
    <>
      <div
        className="flex items-center gap-2 py-2 px-3 hover:bg-muted/50 group transition-colors"
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
      >
        {hasChildren ? (
          <button onClick={() => onToggleExpand(node.id)} className="p-0.5 hover:bg-muted rounded">
            {isExpanded
              ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
              : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </button>
        ) : (
          <span className="w-5" />
        )}

        <Icon className={cn('h-4 w-4', colors.text)} />

        <span className="font-medium truncate flex-1">{node.name}</span>

        {node.code && <span className="text-xs text-muted-foreground">({node.code})</span>}
        {location && <span className="text-xs text-muted-foreground">{location}</span>}

        <Badge className={cn('text-xs text-white', colors.badge)}>{label}</Badge>

        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
          {node.type === 'group' && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onAddChild(node, 'company')} title={`Add ${NODE_TYPE_LABELS['company']}`}>
              <Plus className="h-3 w-3" />
            </Button>
          )}
          {node.type === 'company' && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onAddChild(node, 'company')} title={`Add ${NODE_TYPE_LABELS['company']}`}>
              <Plus className="h-3 w-3" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEdit(node)} title="Edit">
            <Pencil className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => onDelete(node)} title="Delete">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {isExpanded && hasChildren && node.children.map((child) => (
        <ListNode
          key={child.id}
          node={child}
          depth={depth + 1}
          expandedNodes={expandedNodes}
          onToggleExpand={onToggleExpand}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddChild={onAddChild}
        />
      ))}
    </>
  );
}

export function OrgChartView({
  companyType,
  onNodeSelect,
  selectedNodeId: externalSelectedId,
}: OrgChartViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const confirm = useConfirm();

  // State
  const [data, setData] = useState<OrgChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [, setPan] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState<'chart' | 'list'>('list');

  // Config panel state
  const [editingNode, setEditingNode] = useState<OrgChartNode | null>(null);
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);

  // Use external selection if provided
  const activeSelectedId = externalSelectedId ?? selectedNodeId;

  // Fetch hierarchy data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await coreAPI.getHierarchyTree({
        company_type: companyType,
        include_inactive: false,
      });
      // Cast to OrgChartData - types are compatible
      // Backend already collapses single-site divisions server-side
      const chartData = response.data as unknown as OrgChartData;

      // Apply configurable hierarchy labels from API
      if (chartData.labels) {
        applyHierarchyLabels(chartData.labels);
      }

      setData(chartData);

      // Auto-expand first two levels
      const toExpand = new Set<string>();
      const expandLevel = (nodes: OrgChartNode[], depth: number) => {
        nodes.forEach((node) => {
          if (depth < 2 && node.children.length > 0) {
            toExpand.add(node.id);
            expandLevel(node.children, depth + 1);
          }
        });
      };
      expandLevel(chartData.tree, 0);
      setExpandedNodes(toExpand);
    } catch (error) {
      console.error('Failed to fetch hierarchy:', error);
      toast.error('Failed to load organization hierarchy');
    } finally {
      setLoading(false);
    }
  }, [companyType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate layout positions for all visible nodes
  const layoutPositions = useMemo((): LayoutPosition[] => {
    if (!data) return [];

    const positions: LayoutPosition[] = [];
    const { NODE_WIDTH, NODE_HEIGHT, HORIZONTAL_GAP, VERTICAL_GAP } = LAYOUT_CONSTANTS;

    // Calculate width needed for a subtree
    const getSubtreeWidth = (node: OrgChartNode): number => {
      if (!expandedNodes.has(node.id) || node.children.length === 0) {
        return NODE_WIDTH;
      }
      const childrenWidth = node.children.reduce(
        (sum, child) => sum + getSubtreeWidth(child) + HORIZONTAL_GAP,
        -HORIZONTAL_GAP
      );
      return Math.max(NODE_WIDTH, childrenWidth);
    };

    // Position nodes recursively
    const positionNode = (
      node: OrgChartNode,
      depth: number,
      leftOffset: number
    ): number => {
      const subtreeWidth = getSubtreeWidth(node);
      const nodeX = leftOffset + (subtreeWidth - NODE_WIDTH) / 2;
      const nodeY = depth * (NODE_HEIGHT + VERTICAL_GAP);

      positions.push({
        node,
        x: nodeX,
        y: nodeY,
        depth,
      });

      // Position children if expanded
      if (expandedNodes.has(node.id) && node.children.length > 0) {
        let childOffset = leftOffset;
        node.children.forEach((child) => {
          const childWidth = getSubtreeWidth(child);
          positionNode(child, depth + 1, childOffset);
          childOffset += childWidth + HORIZONTAL_GAP;
        });
      }

      return subtreeWidth;
    };

    // Position all root nodes
    let offset = 50; // Starting margin
    data.tree.forEach((rootNode) => {
      const width = positionNode(rootNode, 0, offset);
      offset += width + HORIZONTAL_GAP * 2;
    });

    return positions;
  }, [data, expandedNodes]);

  // Calculate canvas size
  const canvasSize = useMemo(() => {
    if (layoutPositions.length === 0) {
      return { width: 800, height: 600 };
    }
    const maxX = Math.max(...layoutPositions.map((p) => p.x + LAYOUT_CONSTANTS.NODE_WIDTH));
    const maxY = Math.max(...layoutPositions.map((p) => p.y + LAYOUT_CONSTANTS.NODE_HEIGHT));
    return {
      width: maxX + 100,
      height: maxY + 100,
    };
  }, [layoutPositions]);

  // Node interaction handlers
  const handleNodeSelect = (node: OrgChartNode) => {
    setSelectedNodeId(node.id);
    onNodeSelect?.(node);
  };

  const handleToggleExpand = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    if (!data) return;
    const allIds = new Set<string>();
    const collectIds = (nodes: OrgChartNode[]) => {
      nodes.forEach((node) => {
        if (node.children.length > 0) {
          allIds.add(node.id);
          collectIds(node.children);
        }
      });
    };
    collectIds(data.tree);
    setExpandedNodes(allIds);
  };

  const handleCollapseAll = () => {
    setExpandedNodes(new Set());
  };

  const handleZoomIn = () => {
    setZoom((z) => Math.min(z + LAYOUT_CONSTANTS.ZOOM_STEP, LAYOUT_CONSTANTS.MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoom((z) => Math.max(z - LAYOUT_CONSTANTS.ZOOM_STEP, LAYOUT_CONSTANTS.MIN_ZOOM));
  };

  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Parent node reference for creating child entities
  const [parentNodeForCreate, setParentNodeForCreate] = useState<OrgChartNode | null>(null);

  // Open config panel to create a new root-level entity
  const handleAddEntity = (type: NodeType) => {
    const newNode: OrgChartNode = {
      id: `new_${type}_${Date.now()}`,
      entity_id: 0,
      type,
      name: '',
      is_active: true,
      children: [],
      metadata: {},
    };
    setParentNodeForCreate(null);
    setEditingNode(newNode);
    setIsConfigPanelOpen(true);
  };

  // Open the config panel pre-populated for adding a new child node
  const handleAddChild = (parent: OrgChartNode, childType: NodeType) => {
    const newNode: OrgChartNode = {
      id: `new_${childType}_${Date.now()}`,
      entity_id: 0,
      type: childType,
      name: '',
      is_active: true,
      children: [],
      metadata: {},
    };
    setParentNodeForCreate(parent);
    setEditingNode(newNode);
    setIsConfigPanelOpen(true);
    toast.info(`Add ${NODE_TYPE_LABELS[childType]} to ${parent.name}`);
  };

  const handleEdit = (node: OrgChartNode) => {
    setParentNodeForCreate(null);
    setEditingNode(node);
    setIsConfigPanelOpen(true);
  };

  const handleConfigPanelClose = () => {
    setIsConfigPanelOpen(false);
    setEditingNode(null);
  };

  const handleConfigSave = () => {
    // Refresh data after save
    fetchData();
  };

  const handleDelete = async (node: OrgChartNode) => {
    const label = NODE_TYPE_LABELS[node.type];
    const confirmed = await confirm({
      title: `Delete ${label}`,
      description: `Are you sure you want to delete "${node.name}" (${label})? ` +
        (node.children.length > 0
          ? `Warning: This ${label.toLowerCase()} has ${node.children.length} child node(s) which may also be affected.`
          : 'This action cannot be undone.'),
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      const deleteMap: Record<string, (id: number) => Promise<unknown>> = {
        group: coreAPI.deleteCompanyGroup,
        company: coreAPI.deleteCompany,
        site: coreAPI.deleteSite,
        warehouse: coreAPI.deleteWarehouse,
      };
      const deleteFn = deleteMap[node.type];
      if (deleteFn) {
        await deleteFn(node.entity_id);
        toast.success(`${label} "${node.name}" deleted successfully`);
        fetchData();
      } else {
        toast.error(`Cannot delete node of type: ${node.type}`);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(`Failed to delete ${label.toLowerCase()}`);
    }
  };

  // Build connector data
  const connectors = useMemo(() => {
    const result: Array<{
      parentPos: LayoutPosition;
      childPositions: Array<{ x: number; y: number; width: number }>;
    }> = [];

    layoutPositions.forEach((pos) => {
      if (expandedNodes.has(pos.node.id) && pos.node.children.length > 0) {
        const childPositions = pos.node.children
          .map((child) => layoutPositions.find((p) => p.node.id === child.id))
          .filter((p): p is LayoutPosition => p !== undefined)
          .map((p) => ({
            x: p.x,
            y: p.y,
            width: LAYOUT_CONSTANTS.NODE_WIDTH,
          }));

        if (childPositions.length > 0) {
          result.push({
            parentPos: pos,
            childPositions,
          });
        }
      }
    });

    return result;
  }, [layoutPositions, expandedNodes]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.tree.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-96 gap-4">
            <FolderTree className="h-16 w-16 text-muted-foreground" />
            <div className="text-center">
              <h3 className="font-medium">No Organization Structure</h3>
              <p className="text-sm text-muted-foreground">
                Create your first {NODE_TYPE_LABELS['group'].toLowerCase()}, {NODE_TYPE_LABELS['company'].toLowerCase()}, or {NODE_TYPE_LABELS['company'].toLowerCase()} to get started
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleAddEntity('group')}>
                <Layers className="h-4 w-4 mr-2" />
                Add {NODE_TYPE_LABELS['group']}
              </Button>
              <Button variant="outline" onClick={() => handleAddEntity('company')}>
                <Building2 className="h-4 w-4 mr-2" />
                Add {NODE_TYPE_LABELS['company']}
              </Button>
              <Button onClick={() => handleAddEntity('company')}>
                <MapPin className="h-4 w-4 mr-2" />
                Add {NODE_TYPE_LABELS['company']}
              </Button>
            </div>
          </CardContent>
        </Card>
        <NodeConfigPanel
          node={editingNode}
          parentNode={parentNodeForCreate}
          companyType={companyType}
          isOpen={isConfigPanelOpen}
          onClose={handleConfigPanelClose}
          onSave={handleConfigSave}
        />
      </>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              Organization Structure
            </CardTitle>
            <CardDescription>
              {companyType === 'customer' ? 'Customer' : companyType === 'supplier' ? 'Supplier' : 'All'} hierarchy
            </CardDescription>
          </div>

          {/* Stats badges */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-purple-600">
              {data.stats.total_groups} {pluralize(data.labels?.group || NODE_TYPE_LABELS['group'])}
            </Badge>
            <Badge variant="outline" className="text-blue-600">
              {data.stats.total_divisions} {pluralize(data.labels?.division || NODE_TYPE_LABELS['company'])}
            </Badge>
            <Badge variant="outline" className="text-blue-600">
              {data.stats.total_companies} {pluralize(data.labels?.site || NODE_TYPE_LABELS['company'])}
            </Badge>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 pt-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-md">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              title="List view"
              className="rounded-r-none"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'chart' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('chart')}
              title="Chart view"
              className="rounded-l-none"
            >
              <Network className="h-4 w-4" />
            </Button>
          </div>
          <div className="w-px h-6 bg-border mx-2" />
          {viewMode === 'chart' && (
            <>
              <Button variant="outline" size="sm" onClick={handleZoomOut} title="Zoom out">
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground min-w-[60px] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button variant="outline" size="sm" onClick={handleZoomIn} title="Zoom in">
                <ZoomIn className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-border mx-2" />
              <Button variant="outline" size="sm" onClick={handleExpandAll} title="Expand all">
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleCollapseAll} title="Collapse all">
                <Minimize2 className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-border mx-2" />
              <Button variant="outline" size="sm" onClick={handleResetView} title="Reset view">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} title="Refresh data">
            Refresh
          </Button>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add New
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleAddEntity('group')}>
                <Layers className="h-4 w-4 mr-2 text-purple-600" />
                {NODE_TYPE_LABELS['group']}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAddEntity('company')}>
                <Building2 className="h-4 w-4 mr-2 text-blue-600" />
                {NODE_TYPE_LABELS['company']}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAddEntity('company')}>
                <MapPin className="h-4 w-4 mr-2 text-green-600" />
                {NODE_TYPE_LABELS['company']}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {viewMode === 'chart' ? (
          /* Scrollable chart container */
          <div
            ref={containerRef}
            className="relative overflow-auto border-t"
            style={{ height: 'calc(100vh - 400px)', minHeight: '400px' }}
          >
            {/* Canvas with zoom transform */}
            <div
              className="relative"
              style={{
                width: canvasSize.width * zoom,
                height: canvasSize.height * zoom,
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                minWidth: canvasSize.width,
                minHeight: canvasSize.height,
              }}
            >
              {/* SVG layer for connectors */}
              <svg
                className="absolute inset-0 pointer-events-none"
                width={canvasSize.width}
                height={canvasSize.height}
              >
                {connectors.map((conn, idx) => (
                  <OrgChartConnectorGroup
                    key={idx}
                    parentX={conn.parentPos.x}
                    parentY={conn.parentPos.y}
                    parentWidth={LAYOUT_CONSTANTS.NODE_WIDTH}
                    parentHeight={LAYOUT_CONSTANTS.NODE_HEIGHT}
                    children={conn.childPositions}
                  />
                ))}
              </svg>

              {/* Node layer */}
              {layoutPositions.map((pos) => (
                <OrgChartNodeComponent
                  key={pos.node.id}
                  node={pos.node}
                  isSelected={pos.node.id === activeSelectedId}
                  isExpanded={expandedNodes.has(pos.node.id)}
                  hasChildren={pos.node.children.length > 0}
                  onSelect={handleNodeSelect}
                  onToggleExpand={handleToggleExpand}
                  onAddChild={handleAddChild}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  style={{
                    left: pos.x,
                    top: pos.y,
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          /* List view */
          <div className="border-t overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            <div className="divide-y">
              {data.tree.map((node) => (
                <ListNode
                  key={node.id}
                  node={node}
                  depth={0}
                  expandedNodes={expandedNodes}
                  onToggleExpand={handleToggleExpand}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAddChild={handleAddChild}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Node Config Panel */}
      <NodeConfigPanel
        node={editingNode}
        parentNode={parentNodeForCreate}
        isOpen={isConfigPanelOpen}
        onClose={handleConfigPanelClose}
        onSave={handleConfigSave}
      />
    </Card>
  );
}

export default OrgChartView;
